import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../components/api';
import { toast } from 'react-hot-toast';
import {
    Building2, User, Users, Box, Truck, FileText, Camera,
    ClipboardCheck, PackageSearch, Shield, LayoutGrid, Download,
    Users2, FileStack, Loader2, MapPin, Phone, Mail, Store,
    Calendar, Tag, Building, ShieldCheck, Briefcase, UserCheck,
    FileCheck, ChartPie, Construction, AlertTriangle,
    Percent, ClipboardList, Clock
} from 'lucide-react';

// Fonctions utilitaires
const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch {
        return dateString;
    }
};

const formatPercentage = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(1)}%`;
};

const DetailDepotPage = () => {
    const { id } = useParams();
    const [depot, setDepot] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDepotDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                console.log("Fetching depot details for ID:", id);

                const response = await api.get(`/collect/details/${id}/`);
                console.log("API Response:", response);

                setDepot(response.data);
            } catch (err) {
                console.error("Error fetching depot details:", err);
                const errorMessage = err.response?.data?.message || err.message || "Erreur lors du chargement des détails du dépôt";
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchDepotDetails();
        } else {
            setError("ID du dépôt non fourni");
            setLoading(false);
        }
    }, [id]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen">
                    <div className="text-red-600">
                        {error}
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 py-6">
                <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
                    {/* En-tête avec dates importantes */}
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                <div>
                                    <h1 className="text-2xl font-bold text-white">
                                        {depot?.depotinfo?.nom_depot || "Dépôt sans nom"}
                                    </h1>
                                    <div className="mt-3 flex flex-wrap gap-6 text-emerald-50">
                                        <span className="flex items-center gap-2">
                                            <Calendar className="h-5 w-5" />
                                            Créé le: {formatDate(depot?.depotinfo?.date_creation)}
                                        </span>
                                        <span className="flex items-center gap-2">
                                            <Clock className="h-5 w-5" />
                                            Collecté le: {formatDate(depot?.date_collecte)}
                                        </span>
                                        <span className="flex items-center gap-2">
                                            <MapPin className="h-5 w-5" />
                                            {depot?.region}, {depot?.departement}
                                        </span>
                                    </div>
                                </div>
                                <StatusBadge isAuthorized={depot?.depotinfo?.numero_autorisation} />
                            </div>
                        </div>
                        <div className="px-6 py-4 bg-gradient-to-b from-emerald-50 to-white">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                <InfoStat
                                    icon={Building}
                                    label="Surface"
                                    value={`${depot?.infrastructure?.surface_totale || 0} m²`}
                                />
                                <InfoStat
                                    icon={Users}
                                    label="Agents"
                                    value={depot?.ressourceshumaines?.total_agents || 0}
                                />
                                <InfoStat
                                    icon={Store}
                                    label="Type"
                                    value={depot?.depotinfo?.type_vente || '-'}
                                />
                                <InfoStat
                                    icon={Truck}
                                    label="Fournisseurs"
                                    value={depot?.fournisseurs?.length || 0}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Grille principale */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {/* Localisation */}
                        <InfoCard
                            title="Localisation"
                            icon={MapPin}
                            content={[
                                { label: "Commune", value: depot?.commune },
                                { label: "Localité", value: depot?.localite },
                                { label: "Quartier", value: depot?.quartier },
                                { label: "Localisation usuelle", value: depot?.localisation_usuelle },
                                { label: "GPS", value: depot?.gps }
                            ]}
                        />

                        {/* Informations administratives */}
                        <InfoCard
                            title="Informations Administratives"
                            icon={FileText}
                            content={[
                                { label: "RCCM", value: depot?.depotinfo?.rccm },
                                { label: "NIF", value: depot?.depotinfo?.nif },
                                { label: "CNSS", value: depot?.depotinfo?.cnss },
                                { label: "N° Autorisation", value: depot?.depotinfo?.numero_autorisation }
                            ]}
                        />

                        {/* Propriétaire */}
                        <InfoCard
                            title="Propriétaire"
                            icon={User}
                            content={[
                                { label: "Nom", value: depot?.proprietaireinfo?.nom_proprietaire },
                                { label: "Genre", value: depot?.proprietaireinfo?.genre_proprietaire === 'M' ? 'Masculin' : 'Féminin' },
                                { label: "Téléphone", value: depot?.proprietaireinfo?.telephone1 },
                                { label: "Email", value: depot?.proprietaireinfo?.email }
                            ]}
                        />

                        {/* Infrastructure */}
                        <InfoCard
                            title="Infrastructure"
                            icon={Building}
                            content={[
                                { label: "Surface totale", value: `${depot?.infrastructure?.surface_totale} m²` },
                                { label: "Statut du bâtiment", value: depot?.infrastructure?.statut_batiment },
                                { label: "Nature des matériaux", value: depot?.infrastructure?.nature_materiaux },
                                { label: "Source d'énergie", value: depot?.infrastructure?.source_energie }
                            ]}
                            features={[
                                { label: "Climatisation", value: depot?.infrastructure?.climatisation },
                                { label: "Réfrigérateur", value: depot?.infrastructure?.refrigerateur }
                            ]}
                        />

                        {/* Gestion des stocks */}
                        <InfoCard
                            title="Gestion des Stocks"
                            icon={Box}
                            content={[
                                { label: "Système", value: depot?.gestionstock?.type_systeme_gestion },
                                { label: "Rangement", value: depot?.gestionstock?.type_methode_rangement },
                                { label: "Inventaire", value: depot?.gestionstock?.frequence_inventaire }
                            ]}
                        />

                        {/* Ressources Humaines */}
                        <InfoCard
                            title="Ressources Humaines"
                            icon={Users}
                            content={[
                                { label: "Total agents", value: depot?.ressourceshumaines?.total_agents },
                                { label: "Agents permanents", value: depot?.ressourceshumaines?.agents_permanents }
                            ]}
                            features={[
                                { label: "Port d'insignes", value: depot?.ressourceshumaines?.port_insignes }
                            ]}
                        />

                        {/* Clients */}
                        <InfoCard
                            title="Répartition Clients"
                            icon={ChartPie}
                            content={[
                                { label: "Particuliers", value: formatPercentage(depot?.clientdepot?.clients_physiques) },
                                { label: "Centres de santé", value: formatPercentage(depot?.clientdepot?.centres_sante) },
                                { label: "Hôpitaux", value: formatPercentage(depot?.clientdepot?.hopitaux) },
                                { label: "Cliniques", value: formatPercentage(depot?.clientdepot?.cliniques) }
                            ]}
                        />

                        {/* Conformité */}
                        <InfoCard
                            title="Conformité"
                            icon={Shield}
                            content={[
                                { label: "Supervision", value: formatDate(depot?.conformitereglementaire?.date_supervision) },
                                { label: "Résultat", value: depot?.conformitereglementaire?.resultat_supervision }
                            ]}
                            features={[
                                { label: "Plaque visible", value: depot?.documentationvisibilite?.has_plaque }
                            ]}
                        />

                        {/* Aires de stockage */}
                        <InfoCard
                            title="Aires de Stockage"
                            icon={Box}
                            features={[
                                { label: "Réception", value: depot?.airestockage?.aire_reception },
                                { label: "Stockage", value: depot?.airestockage?.aire_stockage },
                                { label: "Produits sensibles", value: depot?.airestockage?.aire_produits_sensibles },
                                { label: "Produits périmés", value: depot?.airestockage?.aire_produits_perimes }
                            ]}
                        />

                        {/* Fournisseurs */}
                        <InfoCard
                            title="Fournisseurs"
                            icon={Truck}
                            className="col-span-full md:col-span-2"
                            content={depot?.fournisseurs?.map(f => ({
                                label: f.nom,
                                value: (
                                    <div className="flex flex-col">
                                        <span>{f.type}</span>
                                        <span className="text-xs text-gray-500">
                                            {f.telephone && <span className="flex items-center gap-1">
                                                <Phone className="h-3 w-3" /> {f.telephone}
                                            </span>}
                                            {f.adresse && <span className="flex items-center gap-1">
                                                <MapPin className="h-3 w-3" /> {f.adresse}
                                            </span>}
                                        </span>
                                    </div>
                                )
                            }))}
                        />

                        {/* Documentation et Visibilité */}
                        <InfoCard
                            title="Documentation"
                            icon={FileText}
                            className="bg-gradient-to-br from-white to-emerald-50"
                            content={[
                                { 
                                    label: "Plaque visible",
                                    value: depot?.documentationvisibilite?.has_plaque ? "Oui" : "Non",
                                    className: depot?.documentationvisibilite?.has_plaque ? "text-emerald-600" : "text-gray-500"
                                }
                            ]}
                            files={[
                                {
                                    label: "Autorisation",
                                    url: depot?.documentationvisibilite?.autorisation_pdf,
                                    name: depot?.documentationvisibilite?.autorisation_pdf_nom_original
                                },
                                {
                                    label: "Photo devanture",
                                    url: depot?.documentationvisibilite?.devanture,
                                    isImage: true
                                }
                            ]}
                        />
                    </div>
                </div>
            </div>
        </Layout>
    );
};

// Nouveau composant pour les statistiques dans l'en-tête
const InfoStat = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-white/50 hover:bg-white transition-colors">
        <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600">
            <Icon className="h-5 w-5" />
        </div>
        <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-lg font-semibold text-gray-900">{value}</p>
        </div>
    </div>
);

// Card améliorée avec support pour les fichiers
const InfoCard = ({ title, icon: Icon, content, features, files, className = "" }) => (
    <div className={`bg-white rounded-lg shadow-sm p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-4">
            {Icon && <Icon className="h-4 w-4 text-emerald-600" />}
            <h2 className="text-sm font-medium text-gray-900">{title}</h2>
        </div>
        
        <div className="space-y-3">
            {content?.map((item, index) => (
                <div key={index} className="flex justify-between text-sm items-start">
                    <span className="text-gray-500">{item.label}</span>
                    <span className={`text-gray-900 font-medium ${item.className || ''}`}>
                        {item.value || '-'}
                    </span>
                </div>
            ))}
            
            {features && (
                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-gray-100">
                    {features.map((feature, index) => (
                        <FeatureBadge
                            key={index}
                            label={feature.label}
                            value={feature.value}
                        />
                    ))}
                </div>
            )}

            {files && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                    {files.map((file, index) => (
                        file?.url ? (
                            <a
                                key={index}
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 text-sm text-emerald-600"
                            >
                                {file.isImage ? (
                                    <Camera className="h-4 w-4" />
                                ) : (
                                    <FileText className="h-4 w-4" />
                                )}
                                {file.label}
                            </a>
                        ) : null
                    ))}
                </div>
            )}
        </div>
    </div>
);

// Badge de statut amélioré
const StatusBadge = ({ isAuthorized }) => (
    <div className={`
        px-4 py-2 rounded-full text-sm font-medium shadow-sm
        ${isAuthorized 
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            : 'bg-orange-50 text-orange-700 border border-orange-200'
        }
    `}>
        {isAuthorized ? 'Dépôt Reconnu' : 'Dépôt Non Reconnu'}
    </div>
);

// Badge de fonctionnalité plus compact
const FeatureBadge = ({ label, value }) => (
    <div className={`
        flex items-center gap-1.5 p-1.5 rounded text-xs
        ${value 
            ? 'bg-green-50 text-green-700' 
            : 'bg-gray-50 text-gray-500'
        }
    `}>
        <div className={`h-1.5 w-1.5 rounded-full ${value ? 'bg-green-500' : 'bg-gray-400'}`} />
        <span>{label}</span>
    </div>
);

export default DetailDepotPage;